import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { ServiceBadge } from "@/components/dashboard/ServiceBadge";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import {
  subscriptionService,
  ESubscriptionStatus,
  ESubscriptionType,
  ESubType,
  EownerType,
} from "@/services/subscription";
import { supplierService } from "@/services/supplier";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Eye,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Truck,
  ExternalLink,
} from "lucide-react";

const PAGE_SIZE = 10;

function generatePaginationNumbers(
  currentPage: number,
  totalPages: number,
): (number | string)[] {
  const delta = 1;
  const range: (number | string)[] = [];
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      range.push(i);
    } else if (range[range.length - 1] !== "...") {
      range.push("...");
    }
  }
  return range;
}

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

export default function SupplierSubscriptions() {
  const navigate = useNavigate();

  const [supplierFilter, setSupplierFilter] = useState<string>("ALL");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [supplierFilter, statusFilter, typeFilter]);

  const { data: suppliersResponse } = useQuery({
    queryKey: ["suppliers", "all"],
    queryFn: () => supplierService.getAll({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });
  const suppliers = suppliersResponse?.data || [];

  const {
    data: subscriptionsResponse,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: [
      "supplier-subscriptions",
      currentPage,
      supplierFilter,
      statusFilter,
      typeFilter,
    ],
    queryFn: () =>
      subscriptionService.getAll({
        page: currentPage,
        limit: PAGE_SIZE,
        ownerType: EownerType.SUPPLIER,
        supplierId:
          supplierFilter !== "ALL" ? Number(supplierFilter) : undefined,
        status:
          statusFilter !== "ALL"
            ? (statusFilter as ESubscriptionStatus)
            : undefined,
        subscriptionType:
          typeFilter !== "ALL" ? (typeFilter as ESubscriptionType) : undefined,
      }),
  });

  const subscriptions = subscriptionsResponse?.data || [];
  const pagination = subscriptionsResponse?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.totalItems || 0;

  return (
    <DashboardLayout
      title="Suscripciones de Proveedores"
      subtitle="Suscripciones asociadas a proveedores de cuentas"
    >
      <div className="space-y-6 animate-fade-in">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-[200px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Proveedor
            </label>
            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los proveedores</SelectItem>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-[160px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Estado
            </label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                <SelectItem value={ESubscriptionStatus.ACTIVE}>
                  Activo
                </SelectItem>
                <SelectItem value={ESubscriptionStatus.EXPIRING}>
                  Por vencer
                </SelectItem>
                <SelectItem value={ESubscriptionStatus.EXPIRED}>
                  Vencido
                </SelectItem>
                <SelectItem value={ESubscriptionStatus.CANCELLED}>
                  Cancelado
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-[160px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Tipo
            </label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                <SelectItem value={ESubscriptionType.SLOT}>Slot</SelectItem>
                <SelectItem value={ESubscriptionType.FULL_ACCOUNT}>
                  Cuenta completa
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              Error al cargar las suscripciones.
            </AlertDescription>
          </Alert>
        )}

        {/* Table */}
        <div className="card-elevated overflow-hidden relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {isLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground mt-2">Cargando...</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold">Proveedor</TableHead>
                  <TableHead className="font-semibold">Servicio</TableHead>
                  <TableHead className="font-semibold">Tipo</TableHead>
                  <TableHead className="font-semibold text-right">
                    Precio
                  </TableHead>
                  <TableHead className="font-semibold">Inicio</TableHead>
                  <TableHead className="font-semibold">Fin</TableHead>
                  <TableHead className="font-semibold text-center">
                    Días rest.
                  </TableHead>
                  <TableHead className="font-semibold">Estado</TableHead>
                  <TableHead className="font-semibold text-right">
                    Acciones
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscriptions.map((sub) => (
                  <TableRow
                    key={sub.id}
                    className="transition-colors hover:bg-muted/30"
                  >
                    {/* Proveedor */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Truck className="h-4 w-4 text-primary" />
                        </div>
                        <button
                          type="button"
                          className="flex items-center gap-1 font-medium text-primary hover:underline focus:outline-none group"
                          onClick={() =>
                            sub.supplier &&
                            navigate(`/suppliers/${sub.supplier.id}`)
                          }
                        >
                          <span>
                            {sub.supplier?.name ||
                              (sub.supplierId
                                ? `Proveedor #${sub.supplierId}`
                                : "—")}
                          </span>
                          {sub.supplier && (
                            <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                          )}
                        </button>
                      </div>
                    </TableCell>

                    {/* Servicio */}
                    <TableCell>
                      {sub.type === ESubType.COMBO ? (
                        <ServiceBadge
                          icon="📦"
                          name={sub.combo?.name || "Combo"}
                        />
                      ) : (
                        <ServiceBadge
                          icon={
                            sub.subscriptionItems[0]?.service?.icon || ""
                          }
                          name={
                            sub.subscriptionItems[0]?.service?.name || "—"
                          }
                        />
                      )}
                    </TableCell>

                    {/* Tipo */}
                    <TableCell className="text-muted-foreground">
                      {sub.type === ESubType.SINGLE
                        ? sub.subscriptionItems[0]?.subscriptionType ===
                          ESubscriptionType.SLOT
                          ? "Slot"
                          : "Cuenta completa"
                        : "Combo"}
                    </TableCell>

                    <TableCell className="text-right font-semibold">
                      ${Number(sub.price).toFixed(0)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(sub.startDate)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(sub.endDate)}
                    </TableCell>

                    {/* Días restantes */}
                    <TableCell className="text-center">
                      <span
                        className={
                          sub.daysRemaining <= 3
                            ? "text-destructive font-semibold"
                            : sub.daysRemaining <= 7
                              ? "text-yellow-600 font-medium"
                              : "text-muted-foreground"
                        }
                      >
                        {sub.daysRemaining}
                      </span>
                    </TableCell>

                    <TableCell>
                      <StatusBadge status={sub.status} />
                    </TableCell>

                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          navigate(`/subscriptions/${sub.id}`)
                        }
                      >
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!isLoading && subscriptions.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">
                No se encontraron suscripciones de proveedores
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Las suscripciones de proveedores se crean al registrar una cuenta
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Mostrando {subscriptions.length} de {totalItems} suscripciones
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || isFetching}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {generatePaginationNumbers(currentPage, totalPages).map(
                  (page, idx) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-2 text-muted-foreground"
                      >
                        ...
                      </span>
                    ) : (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        className="w-9"
                        onClick={() => setCurrentPage(page as number)}
                        disabled={isFetching}
                      >
                        {page}
                      </Button>
                    ),
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages || isFetching}
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
